import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import {
    Reveal,
    Eyebrow,
    SectionHead,
    PrimaryLink,
    OutlineLink,
    CTABand,
    DuotoneImage,
    KineticLines,
} from "@/components/Shared";
import { NetworkGraph, ContourLines } from "@/components/Backdrop";
import HeroEmblem from "@/components/HeroEmblem";
import {
    services,
    triad,
    selectedExpertise,
    connectionChain,
    connectionFormula,
    connectionDigitalNote,
    digitalEvidenceHome,
    followTheMoney,
    whyInfrasic,
} from "@/data/content";

const stats = [
    { value: "10", label: "Integrated Practice Areas" },
    { value: "200+*", label: "Matters Supported" },
    { value: "10+", label: "Years of Experience" },
    { value: "07", label: "Client Segments" },
];

const tickerItems = [
    "INVESTIGATE",
    "ANALYSE",
    "TRACE",
    "EVIDENCE-BASED",
    "FOLLOW THE MONEY",
    "DEFENSIBLE FINDINGS",
];

export default function Home() {
    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="blueprint-grid relative overflow-hidden" data-testid="hero-section">
                <NetworkGraph className="-top-10 right-[-8%] hidden w-[880px] xl:block" />
                <ContourLines className="-left-56 top-[28rem] h-[620px] w-[620px] opacity-[0.14]" rings={12} />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-[#0B1322] to-transparent" />
                <div className="relative mx-auto max-w-7xl px-6 pb-16 pt-36 lg:px-10 lg:pt-44">
                    <div className="grid items-center gap-16 lg:grid-cols-[1.12fr_0.88fr]">
                        <div>
                            <motion.div
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                            >
                                <Eyebrow label="Forensic Investigations | Financial Intelligence | Digital Forensics" testId="hero-overline" />
                            </motion.div>
                            <KineticLines
                                testId="hero-title"
                                delay={0.2}
                                className="mt-7 font-display text-4xl font-bold leading-[1.08] tracking-tight text-[#E8ECF4] sm:text-5xl lg:text-[3.9rem]"
                                lines={[
                                    "Forensic Investigations.",
                                    "Digital Evidence.",
                                    <span key="d" className="italic text-[#5B93E8]">Defensible Findings.</span>,
                                ]}
                            />
                            <motion.div
                                initial={{ opacity: 0, y: 22 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.9, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
                            >
                                <p className="mt-7 max-w-xl text-base leading-relaxed text-[#9AA7BF]">
                                    Infrasic Intelligence (OPC) Private Limited is a specialised
                                    forensic investigation and financial intelligence practice focused
                                    on fraud investigations, financial analysis, digital forensics,
                                    investigative intelligence and complex business matters. We combine
                                    financial analysis, investigative methodology, digital evidence
                                    examination and multidisciplinary expertise to identify
                                    irregularities, reconstruct events, trace financial relationships
                                    and develop evidence-based findings.
                                </p>
                                <p className="mt-5 max-w-xl border-l-[3px] border-[#D9A84E] pl-5 font-display text-lg font-medium text-[#E8ECF4]">
                                    Investigate. Analyse. Trace.
                                </p>
                                <div className="mt-9 flex flex-wrap gap-4">
                                    <PrimaryLink to="/contact" testId="hero-cta-contact">
                                        Discuss an Investigation
                                    </PrimaryLink>
                                    <OutlineLink to="/expertise" testId="hero-cta-expertise">
                                        Explore Our Expertise
                                    </OutlineLink>
                                </div>
                            </motion.div>
                        </div>

                        <HeroEmblem />
                    </div>

                    {/* Stat strip */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.35 }}
                        className="mt-16 grid grid-cols-2 border border-[#243650] bg-[#0F1B2E] lg:grid-cols-4"
                        data-testid="hero-stats"
                    >
                        {stats.map((s, i) => (
                            <div
                                key={s.label}
                                className={`px-7 py-6 ${i !== 0 ? "border-l border-[#243650]" : ""} ${i > 1 ? "max-lg:border-t max-lg:border-[#243650]" : ""} ${i === 2 ? "max-lg:border-l-0" : ""}`}
                            >
                                <p className="font-display text-3xl font-semibold text-[#5B93E8]">{s.value}</p>
                                <p className="mt-1.5 font-tech text-[10px] uppercase tracking-[0.2em] text-[#8C97AC]">
                                    {s.label}
                                </p>
                            </div>
                        ))}
                    </motion.div>
                </div>

                {/* Ticker */}
                <div className="relative overflow-hidden border-y border-[#243650] bg-[#0F1B2E] py-4" data-testid="hero-ticker">
                    <div className="animate-marquee flex w-max whitespace-nowrap">
                        {[...tickerItems, ...tickerItems, ...tickerItems, ...tickerItems].map((item, i) => (
                            <span key={i} className="mx-8 flex items-center gap-8 font-display text-sm italic tracking-[0.12em] text-[#E8ECF4]/70">
                                {item} <span className="h-1.5 w-1.5 bg-[#A87B2F]" />
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* CONNECTED ANALYSIS */}
            <section className="blueprint-grid relative overflow-hidden" data-testid="connected-section">
                <div className="ambient-glow -right-40 -top-24 h-[420px] w-[420px] bg-[#1D4F9C]/25" aria-hidden />
                <div className="ambient-glow -left-40 bottom-0 h-[380px] w-[380px] bg-[#A87B2F]/12" aria-hidden />
                <div className="relative mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28">
                <SectionHead
                    index="02"
                    overline="Connected Analysis"
                    title="Complex investigations require connected analysis."
                    lead="Complex investigations require more than reviewing transactions in isolation. Financial records, corporate relationships, digital information and business activity often need to be examined together."
                    testId="connected"
                />
                <div className="mt-14 grid gap-px border border-[#243650] bg-[#243650] lg:grid-cols-3">
                    {triad.map((t, i) => (
                        <Reveal key={t.title} delay={i * 0.08}>
                            <div
                                className="group h-full border-t-[3px] border-transparent bg-[#0F1B2E] p-8 transition-all duration-300 hover:border-[#D9A84E]"
                                data-testid={`triad-${t.title.toLowerCase()}`}
                            >
                                <p className="outline-numeral font-display text-5xl font-bold">
                                    {String(i + 1).padStart(2, "0")}
                                </p>
                                <h3 className="mt-4 font-display text-xl font-semibold uppercase tracking-wide text-[#E8ECF4]">
                                    {t.title}
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-[#9AA7BF]">{t.description}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.15} className="mt-10">
                    <div className="flex flex-wrap items-center gap-3" data-testid="selected-expertise">
                        <span className="font-tech text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D9A84E]">
                            Selected Expertise
                        </span>
                        {selectedExpertise.map((item, i) => (
                            <Link
                                key={item}
                                to="/expertise"
                                data-testid={`selected-expertise-${i}`}
                                className="border border-white/10 bg-[#0F1B2E] px-4 py-2 font-tech text-[10px] uppercase tracking-[0.16em] text-[#E8ECF4] transition-colors duration-300 hover:border-[#5B93E8] hover:text-[#5B93E8]"
                            >
                                {item}
                            </Link>
                        ))}
                    </div>
                </Reveal>
                </div>
            </section>

            {/* CORE EXPERTISE */}
            <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28" data-testid="expertise-preview-section">
                <div className="flex flex-wrap items-end justify-between gap-8">
                    <SectionHead
                        index="03"
                        overline="Core Expertise"
                        title="10 Integrated Practice Areas"
                        testId="expertise-preview"
                    />
                    <Reveal delay={0.1}>
                        <OutlineLink to="/expertise" testId="expertise-preview-link">
                            All Services
                        </OutlineLink>
                    </Reveal>
                </div>
                <div className="mt-14 grid gap-px border border-[#243650] bg-[#243650] sm:grid-cols-2">
                    {services.map((s, i) => (
                        <Reveal key={s.id} delay={(i % 2) * 0.06}>
                            <Link
                                to={`/expertise#${s.id}`}
                                data-testid={`expertise-card-${s.id}`}
                                className="group flex h-full items-center gap-6 bg-[#0F1B2E] p-7 transition-colors duration-300 hover:bg-[#163050]"
                            >
                                <span className="font-tech text-xs tracking-[0.2em] text-[#D9A84E]">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <span className="border border-[#243650] p-3 transition-colors duration-300 group-hover:border-white/20">
                                    <s.icon className="h-5 w-5 text-[#5B93E8] transition-colors duration-300 group-hover:text-[#D9B36A]" />
                                </span>
                                <span className="font-display text-lg font-semibold text-[#E8ECF4] transition-colors duration-300 group-hover:text-white">
                                    {s.title}
                                </span>
                                <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-[#8C97AC] transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#D9B36A]" />
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* THE INVESTIGATIVE CONNECTION */}
            <section className="relative overflow-hidden bg-[#0A1526]" data-testid="connection-section">
                <NetworkGraph className="left-1/2 top-1/2 w-[1100px] -translate-x-1/2 -translate-y-1/2 opacity-40" animate={false} />
                <div className="relative mx-auto max-w-4xl px-6 py-24 text-center lg:px-10 lg:py-32">
                    <Reveal>
                        <p className="font-tech text-[11px] font-semibold uppercase tracking-[0.3em] text-[#D9B36A]" data-testid="connection-overline">
                            04 — The Investigative Connection
                        </p>
                        <h2 className="mt-6 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                            Fraud rarely exists in one place.
                        </h2>
                        <div className="mx-auto mt-10 max-w-xl space-y-2.5 text-base leading-relaxed text-white/70">
                            {connectionChain.map((line, i) => (
                                <p key={i} data-testid={`connection-line-${i}`}>{line}</p>
                            ))}
                        </div>
                        <p className="mt-10 font-display text-2xl font-semibold italic text-[#D9B36A] sm:text-3xl">
                            We bring these pieces together.
                        </p>
                        <div className="mx-auto mt-8 h-px w-20 bg-[#A87B2F]" />
                        <p className="mx-auto mt-8 max-w-2xl font-tech text-[12px] uppercase leading-loose tracking-[0.18em] text-white/80" data-testid="connection-formula">
                            {connectionFormula}
                        </p>
                        <p className="mx-auto mt-6 max-w-2xl text-sm leading-relaxed text-white/60">
                            {connectionDigitalNote}
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* DIGITAL EVIDENCE + FINANCIAL INTELLIGENCE */}
            <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28" data-testid="evidence-intel-section">
                <div className="grid gap-14 lg:grid-cols-2">
                    <div>
                        <SectionHead
                            index="05"
                            overline="Digital Evidence"
                            title={digitalEvidenceHome.heading}
                            testId="digital-evidence"
                        />
                        <Reveal delay={0.1} className="mt-8 space-y-6 text-base leading-relaxed text-[#9AA7BF]">
                            {digitalEvidenceHome.paragraphs.map((p, i) => (
                                <p key={i}>{p}</p>
                            ))}
                        </Reveal>
                    </div>
                    <div>
                        <SectionHead
                            index="06"
                            overline="Financial Intelligence"
                            title={followTheMoney.heading}
                            testId="follow-the-money"
                        />
                        <Reveal delay={0.1} className="mt-8 space-y-5 text-base leading-relaxed text-[#9AA7BF]">
                            <p className="font-display text-lg font-medium text-[#E8ECF4]">{followTheMoney.lead}</p>
                            <p>{followTheMoney.body}</p>
                        </Reveal>
                        <div className="mt-9 grid gap-px border border-[#243650] bg-[#243650] sm:grid-cols-2">
                            {followTheMoney.groups.map((g, i) => (
                                <Reveal key={g.title} delay={i * 0.05}>
                                    <div className="h-full bg-[#0F1B2E] p-6" data-testid={`money-group-${g.title.toLowerCase()}`}>
                                        <p className="font-tech text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D9A84E]">
                                            {g.title}
                                        </p>
                                        <p className="mt-3 text-sm leading-relaxed text-[#9AA7BF]">{g.description}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
                <Reveal className="mt-16">
                    <p
                        className="mx-auto max-w-3xl text-center font-display text-xl font-medium italic leading-relaxed text-[#5B93E8] sm:text-2xl"
                        data-testid="ledger-device-line"
                    >
                        When the trail moves from the ledger to the device, our investigation follows it.
                    </p>
                </Reveal>
            </section>

            {/* Image band */}
            <DuotoneImage
                src="https://images.unsplash.com/photo-1552133457-ce1d2d33cdfb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxtdW1iYWklMjBza3lsaW5lfGVufDB8fHx8MTc4OTQ5ODAyNHww&ixlib=rb-4.1.0&q=85"
                alt="Mumbai skyline — headquarters of Infrasic Intelligence"
                className="h-[340px] border-y border-[#243650]"
                testId="home-image-band"
            >
                <div className="relative flex h-full flex-col items-center justify-center px-6 text-center">
                    <p className="font-tech text-[10px] uppercase tracking-[0.4em] text-white/70">
                        Mumbai &middot; India
                    </p>
                    <p className="mt-4 max-w-3xl font-display text-2xl font-medium leading-snug text-white sm:text-3xl lg:text-4xl">
                        Every investigation begins with a question. Our role is to find the answer
                        through facts, data and evidence.
                    </p>
                </div>
            </DuotoneImage>

            {/* WHY INFRASIC INTELLIGENCE */}
            <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28" data-testid="why-section">
                <SectionHead
                    index="07"
                    overline="Why Infrasic Intelligence?"
                    title="Built for Complex Investigative Matters."
                    testId="why"
                />
                <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {whyInfrasic.map((p, i) => (
                        <Reveal key={p.title} delay={i * 0.06} className={i === whyInfrasic.length - 1 ? "sm:col-span-2 lg:col-span-3" : ""}>
                            <div
                                className={`card-lift group relative h-full overflow-hidden border border-[#243650] bg-[#0F1B2E] p-8 ${i === whyInfrasic.length - 1 ? "lg:flex lg:items-center lg:gap-10 lg:bg-[#132845]" : ""}`}
                                data-testid={`why-pillar-${i}`}
                            >
                                <span className={`pointer-events-none absolute -right-3 -top-6 font-display text-[7rem] font-bold leading-none ${i === whyInfrasic.length - 1 ? "text-white/[0.06]" : "text-[#E8ECF4]/[0.04]"}`}>
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <div className={i === whyInfrasic.length - 1 ? "relative lg:shrink-0" : "relative"}>
                                    <div className={`inline-flex p-3 transition-colors duration-300 ${i === whyInfrasic.length - 1 ? "border border-[#D9B36A]/40 bg-[#0F1B2E]/5" : "border border-[#5B93E8]/30 bg-[#16263F] group-hover:border-[#D9A84E]/45 group-hover:bg-[#1A2B47]"}`}>
                                        <p.icon className={`h-5 w-5 ${i === whyInfrasic.length - 1 ? "text-[#D9B36A]" : "text-[#5B93E8]"}`} />
                                    </div>
                                </div>
                                <div className="relative">
                                    <h3 className={`mt-5 font-display text-lg font-semibold ${i === whyInfrasic.length - 1 ? "text-white lg:mt-0" : "text-[#E8ECF4]"}`}>
                                        {p.title}
                                    </h3>
                                    <p className={`mt-3 text-sm leading-relaxed ${i === whyInfrasic.length - 1 ? "text-white/65" : "text-[#9AA7BF]"}`}>
                                        {p.description}
                                    </p>
                                    {i !== whyInfrasic.length - 1 && (
                                        <div className="mt-6 h-0.5 w-10 bg-[#243650] transition-all duration-500 group-hover:w-16 group-hover:bg-[#A87B2F]" />
                                    )}
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <CTABand testId="home-cta" />
        </div>
    );
}
